import { useState } from "react";
import { Form, Button, Row, Col, Alert, Spinner } from "react-bootstrap";
import ButtonArrow from "../components/ButtonArrow";
import "../assets/scss/components/custom-form.scss";

function InspectionForm({ inquiryId, inquiryData }) {
    const API = process.env.REACT_APP_API_BASE_URL;

    const formDefault = {
        inquiry: inquiryId || "",
        full_name: inquiryData?.full_name || "",
        email: inquiryData?.email || "",
        phone: inquiryData?.phone || "",
        property_reference: "",
        inspection_type: "",
        attendance_mode: "",
        representative_relationships: [],
        number_of_representatives: null,
        preferred_date: "",
        preferred_time: "",
        fee_disclaimer_acknowledged: false,
        notes: "",
    };

    const [formState, setFormState] = useState(formDefault);
    const [formFeedback, setFormFeedback] = useState({
        status: "",
        message: "",
    });
    const [isLoading, setIsLoading] = useState(null);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormState((prevState) => ({
            ...prevState,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleNumberOfRepresentativesChange = (e) => {
        const value = e.target.value;
        const count = value === "" ? null : Math.max(1, Math.min(2, parseInt(value, 10)));

        setFormState((prevState) => {
            const relationships = [...prevState.representative_relationships];

            if (!count) {
                return {
                    ...prevState,
                    number_of_representatives: null,
                    representative_relationships: [],
                };
            }

            while (relationships.length < count) {
                relationships.push({ relationship: "", other: "" });
            }
            relationships.length = count;

            return {
                ...prevState,
                number_of_representatives: count,
                representative_relationships: relationships,
            };
        });
    };

    const handleRepresentativeRelationshipChange = (index, relationship) => {
        setFormState((prevState) => {
            const relationships = [...prevState.representative_relationships];
            relationships[index] = {
                relationship,
                other: relationship === 'OTHER' ? relationships[index]?.other || "" : "",
            };

            return {
                ...prevState,
                representative_relationships: relationships,
            };
        });
    };

    const handleRepresentativeOtherChange = (index, other) => {
        setFormState((prevState) => {
            const relationships = [...prevState.representative_relationships];
            relationships[index] = {
                ...relationships[index],
                other,
            };

            return {
                ...prevState,
                representative_relationships: relationships,
            };
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Clear previous feedback
        setFormFeedback({
            status: "",
            message: "",
        });
        setIsLoading(true);

        // Validate required fields
        if (!formState.inspection_type || !formState.preferred_date || !formState.preferred_time) {
            setFormFeedback({
                status: "failed",
                message: "Please fill in all required fields.",
            });
            setIsLoading(false);
            return;
        }

        if (formState.inspection_type === 'PHYSICAL') {
            if (!formState.attendance_mode) {
                    setFormFeedback({
                    status: "failed",
                    message: "Please select the inspection attendance mode.",
                });
                setIsLoading(false);
                return;
            }

            if (formState.attendance_mode === 'REPRESENTATIVE') {
                if (!formState.number_of_representatives || formState.number_of_representatives < 1) {
                    setFormFeedback({
                        status: "failed",
                        message: "Please select the number of representatives.",
                    });
                    setIsLoading(false);
                    return;
                }

                const invalidRelationship = formState.representative_relationships.some((rep) => !rep.relationship || (rep.relationship === 'OTHER' && !rep.other));
                if (invalidRelationship) {
                    setFormFeedback({
                        status: "failed",
                        message: "Please specify the relationship for every representative.",
                    });
                    setIsLoading(false);
                    return;
                }
            }
        }

        const payload = {
            inquiry: formState.inquiry,
            full_name: formState.full_name,
            email: formState.email,
            phone: formState.phone,
            property_reference: formState.property_reference || null,
            inspection_type: formState.inspection_type,
            attendance_mode: formState.attendance_mode || null,
            representative_relationships: formState.representative_relationships,
            number_of_representatives: formState.number_of_representatives,
            preferred_date: formState.preferred_date,
            preferred_time: formState.preferred_time,
            fee_disclaimer_acknowledged: formState.fee_disclaimer_acknowledged,
            notes: formState.notes || null,
        };
        console.log("Submitting inspection with payload:", payload);

        // Make API call
        fetch(`${API}/inspections/create/`, {
            method: 'POST',
            body: JSON.stringify(payload),
            headers: {
                'Content-Type': 'application/json',
            },
        })
        .then((response) => {
            if (response.ok) {
                setFormFeedback({
                    status: "success",
                    message: "Inspection booked successfully! We will contact you shortly to confirm.",
                });
                setFormState(formDefault);
            } else {
                setFormFeedback({
                    status: "failed",
                    message: "Something went wrong, please try again.",
                });
            }
            setIsLoading(false);
        })
        .catch((error) => {
            setFormFeedback({
                status: "failed",
                message: "An error has occurred.",
            });
            console.error(`Error in inspection submission: ${error.message}`);
            setIsLoading(false);
        });
    };

    return (
        <Form onSubmit={handleSubmit} className="inspection-form px-4 pt-1 pb-3 border mx-auto">
            {/* Name field */}
            <Form.Group className="mb-4">
                <Form.Label htmlFor="name">Full Name</Form.Label>
                <Form.Control
                    id="name"
                    type="text"
                    name="full_name"
                    placeholder="Title [First name] [Last name]"
                    value={formState.full_name}
                    onChange={handleChange}
                    autoComplete="name"
                    required
                    readOnly={inquiryData?.full_name ? true : false}
                />
            </Form.Group>

            {/* Email and Phone field */}
            <Row xs={1} sm={1} md={2} lg={2} xl={2} xxl={2}>
                <Form.Group as={Col} className="mb-4">
                    <Form.Label htmlFor="email">Email</Form.Label>
                    <Form.Control
                        id="email"
                        type="email"
                        name="email"
                        placeholder="max@gmail.com"
                        value={formState.email}
                        onChange={handleChange}
                        autoComplete="email"
                        required
                        readOnly={inquiryData?.email ? true : false}
                    />
                </Form.Group>

                <Form.Group as={Col} className="mb-4">
                    <Form.Label htmlFor="phone">Phone Number</Form.Label>
                    <Form.Control
                        id="phone"
                        type="tel"
                        name="phone"
                        placeholder="+234**********"
                        value={formState.phone}
                        onChange={handleChange}
                        autoComplete="tel"
                        required
                        readOnly={inquiryData?.phone ? true : false}
                    />
                </Form.Group>
            </Row>

            {/* Property reference field */}
            <Form.Group className="mb-4">
                <Form.Label htmlFor="property-reference">Property name or YouTube link<small className="required text-danger fst-italic ms-1">&#40;optional&#41;</small></Form.Label>
                <Form.Control
                    id="property-reference"
                    type="text"
                    name="property_reference"
                    placeholder="Enter property name or YouTube link"
                    value={formState.property_reference}
                    onChange={handleChange}
                />
            </Form.Group>

            {/* Inspection type and attendance mode */}
            <Row xs={1} sm={1} md={2} lg={2} xl={2} xxl={2}>
                <Form.Group as={Col} className="mb-4">
                    <Form.Label htmlFor="inspection-type">Inspection Type</Form.Label>
                    <Form.Select 
                        id="inspection-type" 
                        name="inspection_type" 
                        className="px-3 py-2" 
                        value={formState.inspection_type} 
                        onChange={handleChange}
                        required
                    >
                        <option value="">Select</option>
                        <option value="PHYSICAL">Physical</option>
                        <option value="VIRTUAL">Virtual</option>
                    </Form.Select>
                </Form.Group>

                {formState.inspection_type === 'PHYSICAL' &&
                    <Form.Group as={Col} className="mb-4">
                        <Form.Label htmlFor="attendance-mode">Inspection Attendance</Form.Label>
                        <Form.Select 
                            id="attendance-mode" 
                            name="attendance_mode" 
                            className="px-3 py-2" 
                            value={formState.attendance_mode} 
                            onChange={handleChange}
                            required
                        >
                            <option value="">Select</option>
                            <option value="IN_PERSON">I will attend in person</option>
                            <option value="REPRESENTATIVE">I will send a representative</option>
                        </Form.Select>
                    </Form.Group>
                }
            </Row>

            {/* Representative relationship field - shown only if REPRESENTATIVE is selected */}
            {formState.inspection_type === 'PHYSICAL' && formState.attendance_mode === 'REPRESENTATIVE' &&
                <>
                    {/* Select number of representatives (1 or 2) */}
                    <Row xs={1} sm={1} md={2} lg={2} xl={2} xxl={2}>
                        {/* Number of representatives selector */}
                        <Form.Group as={Col} className="mb-4">
                            <Form.Label htmlFor="number-of-representatives">Number of Representatives<small className="required text-primary ms-1">(max 2)</small></Form.Label>
                            <Form.Control
                                id="number-of-representatives"
                                type="number"
                                name="number_of_representatives"
                                value={formState.number_of_representatives || ""}
                                onChange={handleNumberOfRepresentativesChange}
                                min={1}
                                max={2}
                                required
                            />
                        </Form.Group>
                    </Row>

                    {/* For each representative, show a relationship selector */}
                    <Row xs={1} sm={1} md={2} lg={2} xl={2} xxl={2}>
                        {Array.from({ length: formState.number_of_representatives || 0 }, (_, index) => (
                            <Form.Group as={Col} className="mb-4"  key={index}>
                                <Form.Label htmlFor={`representative-relationship-${index}`}>
                                    Representative {formState.number_of_representatives > 1 ? index + 1 : ''} Relationship
                                </Form.Label>
                                <Form.Select
                                    id={`representative-relationship-${index}`}
                                    className="px-3 py-2"
                                    value={formState.representative_relationships[index]?.relationship || ""}
                                    onChange={(e) => handleRepresentativeRelationshipChange(index, e.target.value)}
                                    required
                                >
                                    <option value="">Select</option>
                                    <option value="RELATIVE">Relative</option>
                                    <option value="ENGINEER">Engineer</option>
                                    <option value="LAWYER">Lawyer</option>
                                    <option value="FRIEND">Friend</option>
                                    <option value="SURVEYOR">Surveyor</option>
                                    <option value="REALTOR">Realtor</option>
                                    <option value="OTHER">Other</option>
                                </Form.Select>

                                {formState.representative_relationships[index]?.relationship === 'OTHER' && (
                                    <Form.Control
                                        id={`representative-other-${index}`}
                                        type="text"
                                        className="mt-3"
                                        placeholder="Please specify the relationship"
                                        value={formState.representative_relationships[index]?.other || ""}
                                        onChange={(e) => handleRepresentativeOtherChange(index, e.target.value)}
                                        required
                                    />
                                )}
                            </Form.Group>
                        ))}
                    </Row>
                </>
            }

            {/* Preferred date and time */}
            <Row xs={1} sm={1} md={2} lg={2} xl={2} xxl={2}>
                <Form.Group as={Col} className="mb-4">
                    <Form.Label htmlFor="preferred-date">Preferred Inspection Date</Form.Label>
                    <Form.Control
                        id="preferred-date"
                        type="date"
                        name="preferred_date"
                        value={formState.preferred_date}
                        onChange={handleChange}
                        required
                    />
                </Form.Group>

                <Form.Group as={Col} className="mb-4">
                    <Form.Label htmlFor="preferred-time">Preferred Inspection Time</Form.Label>
                    <Form.Control
                        id="preferred-time"
                        type="time"
                        name="preferred_time"
                        value={formState.preferred_time}
                        onChange={handleChange}
                        required
                    />
                </Form.Group>
            </Row>

            {/* Additional Notes field */}
            <Form.Group className="mb-4">
                <Form.Label htmlFor="notes">Additional Notes<small className="required text-danger fst-italic ms-1">&#40;optional&#41;</small></Form.Label>
                <Form.Control
                    id="notes"
                    as="textarea"
                    rows={3}
                    name="notes"
                    placeholder="Any additional information..."
                    value={formState.notes}
                    onChange={handleChange}
                />
            </Form.Group>

            {/* Fee disclaimer checkbox and notes */}
            <Form.Group className="mb-4">
                <Form.Check
                    type="checkbox"
                    id="fee-disclaimer"
                    name="fee_disclaimer_acknowledged"
                    label={<small>I acknowledge that there is an inspection fee starting from ₦30,000, which varies by location and number of properties. This fee is refundable after purchase.</small>}
                    checked={formState.fee_disclaimer_acknowledged}
                    onChange={handleChange}
                    required
                />
            </Form.Group>

            <Button
                className={`d-flex align-items-center justify-content-center gap-3 w-100  my-4`} type="submit"
                disabled={ isLoading }
            >
                {isLoading &&
                    <Spinner animation="border" variant="white" size="sm" />
                }
                Book Inspection { !isLoading &&<ButtonArrow /> }
            </Button>

            {formFeedback.status != null && (
                <Alert variant={formFeedback.status === "success" ? "primary" : "danger"} className={`${formFeedback.status} my-3 pt-3 notification`}>
                    {formFeedback.message}
                </Alert>
            )}
        </Form>
    );
}

export default InspectionForm;
